import type { Column, GridApi } from 'ag-grid-community'
import { useDeferredValue, useEffect, useState } from 'react'

/** One toggleable column, as the chooser needs to render it. */
export interface ToolbarColumn {
  colId: string
  headerName: string
  visible: boolean
}

export interface UseGridToolbarArgs {
  /** Null until `onGridReady` fires. Every action no-ops until then. */
  api: GridApi | null
}

export interface UseGridToolbarResult {
  searchText: string
  setSearchText: (text: string) => void
  columns: ToolbarColumn[]
  setColumnVisible: (colId: string, visible: boolean) => void
  resetColumns: () => void
  /** True when a column filter or the search box is narrowing the grid. */
  isFiltered: boolean
  clearFilters: () => void
  /** False while the grid is still starting up. */
  isReady: boolean
}

/** An api survives its grid's unmount; calling into a destroyed one throws. */
const usable = (api: GridApi | null): api is GridApi =>
  api !== null && !api.isDestroyed()

/*
 * `getColumns` returns the columns built from `columnDefs` only — AG Grid's own
 * selection column is not among them — but they are not all DATA: the pinned
 * "Open" column is an action, and offering it in the chooser lets an admin hide
 * the grid's only route into a game.
 *
 * `suppressColumnsToolPanel` is the flag AG Grid already gives a column that
 * does not belong in a column chooser, so it is what the rule reads.
 */
const isDataColumn = (column: Column): boolean =>
  column.getColDef().suppressColumnsToolPanel !== true

const readColumns = (api: GridApi): ToolbarColumn[] =>
  (api.getColumns() ?? []).filter(isDataColumn).map((column) => {
    const colDef = column.getColDef()
    return {
      colId: column.getColId(),
      headerName: colDef.headerName ?? column.getColId(),
      visible: column.isVisible(),
    }
  })

/**
 * The Details grid's toolbar logic: search and column visibility.
 *
 * The search text lives HERE rather than on the grid, and reaches AG Grid
 * through `setGridOption` rather than a prop. That is the reference grid's own
 * arrangement (`ag-grid-demos/performance`) and it is not cosmetic: a
 * `quickFilterText` prop would re-render the grid component on every keystroke,
 * and re-rendering a virtualized grid mid-type is exactly what makes a search
 * box feel broken. `useDeferredValue` then keeps the input responsive while the
 * filter pass runs behind it.
 */
export const useGridToolbar = ({
  api,
}: UseGridToolbarArgs): UseGridToolbarResult => {
  'use memo'

  const [searchText, setSearchText] = useState('')
  const [columns, setColumns] = useState<ToolbarColumn[]>([])
  const [isFiltered, setIsFiltered] = useState(false)

  const deferredSearchText = useDeferredValue(searchText)

  useEffect(() => {
    if (!usable(api)) {
      return
    }
    api.setGridOption('quickFilterText', deferredSearchText)
  }, [api, deferredSearchText])

  //  Column state is read back from the grid rather than mirrored, so a reset,
  //  a drag-reorder or a future saved layout all land in the chooser without a
  //  second source of truth to keep in step.
  useEffect(() => {
    if (!usable(api)) {
      return
    }

    const syncColumns = () => {
      if (usable(api)) {
        setColumns(readColumns(api))
      }
    }
    const syncFilter = () => {
      if (usable(api)) {
        setIsFiltered(api.isAnyFilterPresent())
      }
    }

    syncColumns()
    syncFilter()

    api.addEventListener('columnVisible', syncColumns)
    api.addEventListener('gridColumnsChanged', syncColumns)
    api.addEventListener('filterChanged', syncFilter)

    return () => {
      if (!usable(api)) {
        return
      }
      api.removeEventListener('columnVisible', syncColumns)
      api.removeEventListener('gridColumnsChanged', syncColumns)
      api.removeEventListener('filterChanged', syncFilter)
    }
  }, [api])

  const setColumnVisible = (colId: string, visible: boolean) => {
    if (!usable(api)) {
      return
    }
    api.setColumnsVisible([colId], visible)
  }

  const resetColumns = () => {
    if (!usable(api)) {
      return
    }
    api.resetColumnState()
  }

  const clearFilters = () => {
    setSearchText('')
    if (!usable(api)) {
      return
    }
    api.setFilterModel(null)
  }

  return {
    searchText,
    setSearchText,
    columns,
    setColumnVisible,
    resetColumns,
    isFiltered,
    clearFilters,
    isReady: usable(api),
  }
}
